import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Brain, Check, Loader2, Pencil, Plus, Trash2, Users, User, X } from 'lucide-react';
import { klarysUrl } from './klarysApi';

const CATS = { preference: 'Préférence', regle: 'Règle métier', correction: 'Correction', vocabulaire: 'Vocabulaire', autre: 'Autre' };
const SOURCES = { auto: 'appris automatiquement', manuel: 'ajouté manuellement', avis: 'issu d\'un avis négatif' };

const Ligne = ({ m, modifiable, onMaj, onSuppr, i }) => {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(m.texte);
  return (
    <li className={`rounded-lg border px-3 py-2 ${m.actif ? 'border-slate-200 bg-white' : 'border-dashed border-slate-200 bg-slate-50 opacity-70'}`} data-testid={`klarys-memoire-item-${m.portee}-${i}`}>
      {edit ? (
        <div className="flex gap-2">
          <textarea value={v} onChange={(e) => setV(e.target.value)} rows={2} className="flex-1 rounded border border-slate-300 p-1.5 text-xs" data-testid={`klarys-memoire-edit-input-${m.portee}-${i}`} />
          <button type="button" onClick={async () => { if (await onMaj(m.id, { texte: v })) setEdit(false); }} className="text-emerald-700" aria-label="Enregistrer" data-testid={`klarys-memoire-edit-save-${m.portee}-${i}`}><Check className="h-4 w-4" /></button>
          <button type="button" onClick={() => { setEdit(false); setV(m.texte); }} className="text-slate-500" aria-label="Annuler"><X className="h-4 w-4" /></button>
        </div>
      ) : <p className="text-sm text-slate-800" data-testid={`klarys-memoire-texte-${m.portee}-${i}`}>{m.texte}</p>}
      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
        <span className="rounded bg-slate-100 px-1.5">{CATS[m.categorie] || 'Autre'}</span>
        <span>{SOURCES[m.source] || m.source} · {new Date(m.updated_at).toLocaleDateString('fr-FR')}{m.par ? ` · ${m.par}` : ''}</span>
        {modifiable && !edit && (
          <span className="ml-auto flex items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-1"><input type="checkbox" checked={m.actif} onChange={(e) => onMaj(m.id, { actif: e.target.checked })} className="accent-[#0a3d62]" data-testid={`klarys-memoire-actif-${m.portee}-${i}`} /> active</label>
            <button type="button" onClick={() => setEdit(true)} className="hover:text-[#0a3d62]" data-testid={`klarys-memoire-modifier-${m.portee}-${i}`}><Pencil className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => onSuppr(m.id)} className="hover:text-rose-600" data-testid={`klarys-memoire-suppr-${m.portee}-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </span>
        )}
      </div>
    </li>
  );
};

export const KlarysMemoire = ({ headers }) => {
  const [data, setData] = useState(null);
  const [texte, setTexte] = useState('');
  const [portee, setPortee] = useState('perso');
  const [categorie, setCategorie] = useState('preference');
  const [busy, setBusy] = useState(false);
  const charger = () => axios.get(klarysUrl('/memoire'), { headers }).then((r) => setData(r.data)).catch(() => toast.error('Mémoire indisponible'));
  useEffect(() => { charger(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const ajouter = async () => {
    setBusy(true);
    try { const r = await axios.post(klarysUrl('/memoire'), { texte, portee, categorie }, { headers }); toast.success(r.data.deja ? 'Déjà en mémoire' : 'Ajouté à la mémoire'); setTexte(''); charger(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Ajout impossible'); } finally { setBusy(false); }
  };
  const maj = async (id, p) => {
    try { await axios.patch(klarysUrl(`/memoire/${id}`), p, { headers }); charger(); return true; } catch (e) { toast.error(e.response?.data?.detail || 'Modification impossible'); return false; }
  };
  const suppr = async (id) => {
    try { await axios.delete(klarysUrl(`/memoire/${id}`), { headers }); toast.success('Supprimé de la mémoire'); charger(); } catch (e) { toast.error(e.response?.data?.detail || 'Suppression impossible'); }
  };
  if (!data) return <div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#0a3d62]" /></div>;
  const sa = data.super_admin;
  const groupe = (p) => data.items.filter((m) => m.portee === p);
  const Section = ({ p, titre, Icon, max, modifiable }) => (
    <section className="space-y-2" data-testid={`klarys-memoire-${p}`}>
      <h4 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Icon className="h-4 w-4 text-[#0a3d62]" /> {titre}
        <span className="font-normal text-slate-500">({groupe(p).length} / {max}){!modifiable ? ' — modifiable par le super admin' : ''}</span></h4>
      {groupe(p).length ? <ul className="space-y-1.5">{groupe(p).map((m, i) => <Ligne key={m.id} m={m} i={i} modifiable={modifiable} onMaj={maj} onSuppr={suppr} />)}</ul>
        : <p className="text-xs text-slate-500">Rien d'appris pour l'instant.</p>}
    </section>
  );
  return (
    <div className="h-full space-y-5 overflow-y-auto bg-[#f6f8fb] p-4" data-testid="klarys-memoire">
      <div className="rounded-xl border border-sky-100 bg-sky-50 p-3 text-xs text-sky-900">
        <p className="flex items-center gap-1.5 font-semibold"><Brain className="h-4 w-4" /> Klarys apprend de vos échanges</p>
        <p className="mt-1">Il retient automatiquement vos préférences, corrections et règles métier, et les applique dans toutes les conversations suivantes. Un avis « Pas utile » avec commentaire devient aussi une correction. Vérifiez, modifiez ou désactivez ici ce qu'il a appris.</p>
      </div>
      <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3" data-testid="klarys-memoire-form">
        <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={2} maxLength={500} placeholder="Ex. : Toujours signer les courriers « Service adhésions — OBJECTIF SCOP OUTREMER »"
          className="w-full rounded-md border border-slate-300 p-2 text-sm" data-testid="klarys-memoire-input" />
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <select value={portee} onChange={(e) => setPortee(e.target.value)} className="rounded border border-slate-300 px-2 py-1" data-testid="klarys-memoire-portee">
            <option value="perso">Mémoire personnelle</option>
            {sa && <option value="commune">Mémoire commune (toute l'administration)</option>}
          </select>
          <select value={categorie} onChange={(e) => setCategorie(e.target.value)} className="rounded border border-slate-300 px-2 py-1" data-testid="klarys-memoire-categorie">
            {Object.entries(CATS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <button type="button" onClick={ajouter} disabled={busy || texte.trim().length < 5} data-testid="klarys-memoire-ajouter"
            className="ml-auto inline-flex items-center gap-1 rounded-md bg-[#0a3d62] px-3 py-1.5 font-semibold text-white hover:bg-[#0c4a77] disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Ajouter
          </button>
        </div>
      </div>
      {Section({ p: 'commune', titre: 'Mémoire commune', Icon: Users, max: data.max_commune, modifiable: sa })}
      {Section({ p: 'perso', titre: 'Ma mémoire personnelle', Icon: User, max: data.max_perso, modifiable: true })}
    </div>
  );
};
